import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryListStatusAtom,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { deleteGiftDeliveryCategory } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.controller';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import { GiftDeliveryCategoryData } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.interface';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { Link } from 'react-router-dom';

export const GiftDeliveryCategoryListPage: FC = function GiftDeliveryCategoryListPage() {
  const { sellerRole } = useAuth();

  useGiftDeliveryCategoryListHook(sellerRole());

  const status = useRecoilValue(giftDeliveryCategoryListStatusAtom);
  const data = useRecoilValue(giftDeliveryCategoryListDataAtom);
  const setData = useSetRecoilState(giftDeliveryCategoryListDataAtom);

  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!searchQuery.trim()) return data;

    const query = searchQuery.toLowerCase().trim();
    return data.filter((item) => item.name.toLowerCase().includes(query));
  }, [data, searchQuery]);

  const handleDelete = useCallback(
    async (category: GiftDeliveryCategoryData) => {
      if (!confirm(`"${category.name}" 카테고리를 삭제하시겠습니까?`)) return;

      try {
        setDeletingId(category._id);
        await deleteGiftDeliveryCategory(category._id, sellerRole());
        setData((prev) => prev.filter((item) => item._id !== category._id));
        alert('삭제되었습니다.');
      } catch (error) {
        console.error(error);
        alert('삭제에 실패했습니다.');
      } finally {
        setDeletingId(null);
      }
    },
    [sellerRole, setData],
  );

  const itemListRender = useMemo(() => {
    if (status === 'success') {
      if (filteredData.length === 0) {
        return (
          <div className="py-8 text-center text-gray-500">
            {searchQuery ? '검색 결과가 없습니다.' : '등록된 배송 카테고리가 없습니다.'}
          </div>
        );
      }

      return (
        <div className="space-y-2">
          {filteredData.map((item) => (
            <div
              key={item._id}
              className="flex items-center justify-between rounded-lg border bg-white p-4 hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                {item.image && typeof item.image === 'object' && item.image.url ? (
                  <img
                    src={fileBucketUrl(item.image.url, 'raw')}
                    alt={item.name}
                    className="h-12 w-12 rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
                    <span className="text-lg font-bold">{item.name.charAt(0)}</span>
                  </div>
                )}
                <div>
                  <p className="font-medium">{item.name}</p>
                  <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-gray-500">
                    {item.order != null && <span>순서: {item.order}</span>}
                    {item.createdAt && (
                      <span>
                        {new Date(item.createdAt).toLocaleDateString('ko-KR')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/gift-seller/delivery-category/${item._id}/edit`}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                >
                  수정
                </Link>
                <button
                  onClick={() => handleDelete(item)}
                  disabled={deletingId === item._id}
                  className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {deletingId === item._id ? '삭제 중...' : '삭제'}
                </button>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="center-box h-screen-15">
        <Loading />
      </div>
    );
  }, [status, filteredData, searchQuery, deletingId, handleDelete]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">배송 카테고리 관리</h1>
          <Link to="/gift-seller/delivery-category/new">
            <Button className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white">
              + 카테고리 등록
            </Button>
          </Link>
        </div>

        {status === 'success' && (
          <div className="mt-3 text-sm text-gray-600">
            전체 <span className="font-medium text-gray-900">{data?.length || 0}</span>개
          </div>
        )}
      </div>

      <div className="border-b bg-white px-6 py-3">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="카테고리명 검색..."
            className="w-full rounded-lg border px-3 py-2 pl-9 text-sm"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            🔍
          </span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>

        {status === 'success' && searchQuery && (
          <div className="mt-2 text-xs text-gray-500">
            {filteredData.length}개 표시 (전체 {data?.length || 0}개 중)
          </div>
        )}
      </div>

      <div className="p-4">{itemListRender}</div>
    </div>
  );
};
